<template>
  <v-container fluid>
    <v-card v-if="record" variant="outlined" class="details-page-card">
      <v-card-title class="details-header d-flex flex-wrap align-center ga-3">
        <div>
          <div class="text-h6 font-weight-bold">Production Material Issue {{ record.code }}</div>
          <div class="text-caption text-medium-emphasis">Organized issue summary with separate raw and packaging material allocations</div>
        </div>
        <v-spacer />
        <v-chip :color="approvalColor(record.approval_status)" size="small" variant="flat">
          {{ record.approval_status || (record.status ? 'Pending' : 'Draft') }}
        </v-chip>
        <v-btn class="d-print-none" icon="mdi-printer" variant="text" @click="printPage" />
      </v-card-title>

      <v-card-text class="pt-4">
        <v-row dense class="mb-3">
          <v-col cols="12" md="3"><Info icon="mdi-file-document-outline" label="Requisition" :value="record.requisition_master?.code || '-'" /></v-col>
          <v-col cols="12" md="3"><Info icon="mdi-calendar" label="Issue Date" :value="dateOnly(record.requisition_delivery_date) || '-'" /></v-col>
          <v-col cols="12" md="3"><Info icon="mdi-warehouse" label="Warehouse" :value="record.warehouse?.name || '-'" /></v-col>
          <v-col cols="12" md="3"><Info icon="mdi-currency-bdt" label="Total Issue Cost" :value="number(record.amount, 2)" /></v-col>
          <v-col cols="12"><Info icon="mdi-note-text-outline" label="Remarks" :value="record.remarks || '-'" /></v-col>
        </v-row>

        <v-row dense class="mb-4">
          <v-col cols="6" md="3"><StatCard title="Total Items" :value="details.length" subtitle="All issued materials" /></v-col>
          <v-col cols="6" md="3"><StatCard title="Raw Materials" :value="grouped.raw.items.length" :subtitle="`Cost ${number(grouped.raw.cost, 2)}`" tone="teal" /></v-col>
          <v-col cols="6" md="3"><StatCard title="Packaging Materials" :value="grouped.packaging.items.length" :subtitle="`Cost ${number(grouped.packaging.cost, 2)}`" tone="indigo" /></v-col>
          <v-col cols="6" md="3"><StatCard title="Allocations" :value="allocationCount" subtitle="Total lot lines" tone="orange" /></v-col>
        </v-row>

        <section class="detail-section mb-5">
          <div class="section-header raw-header d-flex align-center ga-2 px-4 py-3 mb-3">
            <v-icon icon="mdi-flask-outline" size="small" />
            <div>
              <div class="text-subtitle-1 font-weight-bold">Raw Materials (RM)</div>
              <div class="text-caption text-medium-emphasis">Purity-adjusted production inputs including balancer materials</div>
            </div>
            <v-spacer />
            <v-chip size="small" color="teal" variant="tonal">{{ grouped.raw.items.length }} item(s)</v-chip>
          </div>

          <v-alert v-if="grouped.raw.items.length === 0" type="info" variant="tonal">No raw material lines found.</v-alert>

          <v-expansion-panels v-else multiple class="detail-panels">
            <v-expansion-panel
              v-for="detail in grouped.raw.items"
              :key="detail.id"
              :title="null"
              elevation="0"
            >
              <v-expansion-panel-title>
                <div class="w-100 d-flex flex-wrap align-center ga-2 pr-3">
                  <div>
                    <div class="font-weight-bold">{{ detail.product?.code }} - {{ detail.product?.name }}</div>
                    <div class="text-caption text-medium-emphasis">{{ labelize(detail.item_type || 'raw_material') }} · {{ labelize(detail.calculation_method || 'fixed') }}</div>
                  </div>
                  <v-chip size="x-small" color="teal" variant="tonal">RM</v-chip>
                  <v-chip size="x-small" :color="(detail.calculation_method || 'fixed') === 'balancer' ? 'orange' : 'blue'" variant="tonal">
                    {{ labelize(detail.calculation_method || 'fixed') }}
                  </v-chip>
                  <v-spacer />
                  <div class="text-caption text-end">
                    <div>Physical: <strong>{{ number(detail.qty) }}</strong></div>
                    <div>Cost: <strong>{{ number(detail.amount, 2) }}</strong></div>
                  </div>
                </div>
              </v-expansion-panel-title>
              <v-expansion-panel-text>
                <v-row dense class="mb-3">
                  <v-col cols="6" md="3"><Metric label="Standard Purity" :value="`${number(detail.standard_purity, 4)}%`" /></v-col>
                  <v-col cols="6" md="3"><Metric label="Physical Qty" :value="number(detail.qty)" /></v-col>
                  <v-col cols="6" md="3"><Metric label="Required Active" :value="number(detail.required_active_quantity, 6)" /></v-col>
                  <v-col cols="6" md="3"><Metric label="Issued Active" :value="number(detail.issued_active_quantity, 6)" /></v-col>
                </v-row>
                <LotTable :allocations="detail.allocations || []" />
              </v-expansion-panel-text>
            </v-expansion-panel>
          </v-expansion-panels>
        </section>

        <section class="detail-section">
          <div class="section-header packaging-header d-flex align-center ga-2 px-4 py-3 mb-3">
            <v-icon icon="mdi-package-variant-closed" size="small" />
            <div>
              <div class="text-subtitle-1 font-weight-bold">Packaging Materials (PM)</div>
              <div class="text-caption text-medium-emphasis">Packaging components issued with quantity-based costing</div>
            </div>
            <v-spacer />
            <v-chip size="small" color="indigo" variant="tonal">{{ grouped.packaging.items.length }} item(s)</v-chip>
          </div>

          <v-alert v-if="grouped.packaging.items.length === 0" type="info" variant="tonal">No packaging material lines found.</v-alert>

          <v-expansion-panels v-else multiple class="detail-panels">
            <v-expansion-panel
              v-for="detail in grouped.packaging.items"
              :key="detail.id"
              elevation="0"
            >
              <v-expansion-panel-title>
                <div class="w-100 d-flex flex-wrap align-center ga-2 pr-3">
                  <div>
                    <div class="font-weight-bold">{{ detail.product?.code }} - {{ detail.product?.name }}</div>
                    <div class="text-caption text-medium-emphasis">Packaging issue line</div>
                  </div>
                  <v-chip size="x-small" color="indigo" variant="tonal">PM</v-chip>
                  <v-spacer />
                  <div class="text-caption text-end">
                    <div>Physical: <strong>{{ number(detail.qty) }}</strong></div>
                    <div>Cost: <strong>{{ number(detail.amount, 2) }}</strong></div>
                  </div>
                </div>
              </v-expansion-panel-title>
              <v-expansion-panel-text>
                <v-row dense class="mb-3">
                  <v-col cols="6" md="3"><Metric label="Allocation Basis" value="Quantity" /></v-col>
                  <v-col cols="6" md="3"><Metric label="Physical Qty" :value="number(detail.qty)" /></v-col>
                  <v-col cols="6" md="3"><Metric label="Required Active" :value="number(detail.required_active_quantity, 6)" /></v-col>
                  <v-col cols="6" md="3"><Metric label="Issued Active" :value="number(detail.issued_active_quantity, 6)" /></v-col>
                </v-row>
                <LotTable :allocations="detail.allocations || []" :packaging="true" />
              </v-expansion-panel-text>
            </v-expansion-panel>
          </v-expansion-panels>
        </section>
      </v-card-text>
    </v-card>
    <v-skeleton-loader v-else type="article, table" />
  </v-container>
</template>

<script setup>
import { computed, defineComponent, h, onMounted, ref } from "vue";
import { useRoute } from "vue-router";
import { useStore } from "vuex";
import { toast } from "vue3-toastify";

const Info = defineComponent({
  props: { label: String, value: [String, Number], icon: String },
  setup(props) {
    return () => h("div", { class: "info-box" }, [
      h("div", { class: "d-flex align-center ga-2 mb-1" }, [
        props.icon ? h("i", { class: `mdi ${props.icon} info-icon` }) : null,
        h("div", { class: "info-label" }, props.label),
      ]),
      h("div", { class: "font-weight-medium info-value" }, props.value ?? "-"),
    ]);
  },
});

const StatCard = defineComponent({
  props: { title: String, value: [String, Number], subtitle: String, tone: String },
  setup(props) {
    return () => h("div", { class: ['stat-card', props.tone ? `stat-card--${props.tone}` : ''] }, [
      h("div", { class: "stat-title" }, props.title),
      h("div", { class: "stat-value" }, String(props.value ?? '-')),
      h("div", { class: "stat-subtitle" }, props.subtitle ?? ''),
    ]);
  },
});

const Metric = defineComponent({
  props: { label: String, value: [String, Number] },
  setup(props) {
    return () => h("div", { class: "metric-card" }, [
      h("div", { class: "metric-label" }, props.label),
      h("div", { class: "metric-value" }, props.value ?? '-'),
    ]);
  },
});

const LotTable = defineComponent({
  props: { allocations: { type: Array, default: () => [] }, packaging: Boolean },
  setup(props) {
    return () => h('div', { class: 'lot-table-wrap' }, [
      props.allocations?.length
        ? h('table', { class: 'lot-table' }, [
            h('thead', {}, [h('tr', {}, [
              h('th', {}, 'Lot / Inventory'),
              h('th', {}, 'Batch'),
              h('th', {}, 'Manufactured'),
              h('th', {}, 'Expiry'),
              h('th', { class: 'text-center' }, props.packaging ? 'Basis' : 'Purchase Purity'),
              h('th', { class: 'text-end' }, 'Qty'),
              h('th', { class: 'text-end' }, 'Active Qty'),
              h('th', { class: 'text-end' }, 'Unit Cost'),
              h('th', { class: 'text-end' }, 'Amount'),
            ])]),
            h('tbody', {}, props.allocations.map((allocation) => h('tr', { key: allocation.id }, [
              h('td', {}, allocation.lot_no || '-'),
              h('td', {}, allocation.batch_no || '-'),
              h('td', {}, dateOnly(allocation.manufacturing_date) || '-'),
              h('td', {}, dateOnly(allocation.expiry_date) || '-'),
              h('td', { class: 'text-center' }, props.packaging ? 'Quantity' : `${number(allocation.purchase_purity, 4)}%`),
              h('td', { class: 'text-end' }, number(allocation.quantity)),
              h('td', { class: 'text-end' }, number(allocation.active_quantity, 6)),
              h('td', { class: 'text-end' }, number(allocation.unit_cost, 6)),
              h('td', { class: 'text-end font-weight-medium' }, number(allocation.amount, 2)),
            ]))),
          ])
        : h('div', { class: 'empty-lots' }, 'No lot allocation rows found.'),
    ]);
  },
});

const route = useRoute();
const store = useStore();
const record = ref(null);

const details = computed(() => record.value?.requisition_delivery_details || []);

const grouped = computed(() => {
  const raw = details.value.filter((detail) => (detail.item_type || 'raw_material') !== 'packaging');
  const packaging = details.value.filter((detail) => (detail.item_type || 'raw_material') === 'packaging');
  return {
    raw: {
      items: raw,
      cost: raw.reduce((sum, item) => sum + Number(item.amount || 0), 0),
    },
    packaging: {
      items: packaging,
      cost: packaging.reduce((sum, item) => sum + Number(item.amount || 0), 0),
    },
  };
});

const allocationCount = computed(() => details.value.reduce((sum, detail) => sum + (detail.allocations?.length || 0), 0));

function number(value, digits = 4) {
  return Number(value || 0).toLocaleString(undefined, { maximumFractionDigits: digits });
}

function dateOnly(value) {
  return value ? String(value).slice(0, 10) : "";
}

function approvalColor(status) {
  if (status === "Approved") return "success";
  if (status === "Cancelled" || status === "Rejected") return "error";
  return "warning";
}

function printPage() {
  window.print();
}

function labelize(value) {
  return String(value || '')
    .replaceAll('_', ' ')
    .replace(/\b\w/g, (m) => m.toUpperCase());
}

onMounted(async () => {
  try {
    record.value = await store.dispatch("getInvProductionRequisitionDeliveryById", route.params.id);
  } catch (error) {
    toast.error(error?.response?.data?.message || "Material issue details could not be loaded.");
  }
});
</script>

<style scoped>
.details-page-card { border-radius: 10px; overflow: hidden; }
.details-header { background: #eef3f7; border-bottom: 1px solid rgba(0,0,0,.06); }
.info-box { border: 1px solid rgba(0, 0, 0, 0.1); border-radius: 10px; min-height: 84px; padding: 10px 12px; background: #fff; }
.info-label { color: rgba(0, 0, 0, 0.6); font-size: 0.72rem; text-transform: uppercase; letter-spacing: .4px; }
.info-icon { color: rgba(0,0,0,.55); font-size: 16px; }
.info-value { font-size: .95rem; }
.stat-card { border: 1px solid rgba(0,0,0,.08); border-radius: 12px; padding: 12px 14px; background: linear-gradient(180deg, #ffffff 0%, #fafafa 100%); min-height: 96px; }
.stat-card--teal { background: linear-gradient(180deg, #f2fffb 0%, #ecfdf8 100%); }
.stat-card--indigo { background: linear-gradient(180deg, #f6f7ff 0%, #eff1ff 100%); }
.stat-card--orange { background: linear-gradient(180deg, #fff8f1 0%, #fff2e5 100%); }
.stat-title { font-size: .78rem; color: rgba(0,0,0,.6); text-transform: uppercase; letter-spacing: .4px; }
.stat-value { font-size: 1.4rem; font-weight: 700; line-height: 1.2; margin-top: 4px; }
.stat-subtitle { margin-top: 4px; font-size: .82rem; color: rgba(0,0,0,.65); }
.section-header { border: 1px solid rgba(0,0,0,.08); border-radius: 12px; }
.raw-header { background: #f0faf7; }
.packaging-header { background: #f3f4ff; }
.detail-panels :deep(.v-expansion-panel) { border: 1px solid rgba(0,0,0,.08); border-radius: 10px !important; margin-bottom: 12px; overflow: hidden; }
.detail-panels :deep(.v-expansion-panel-title) { min-height: 72px; padding: 14px 16px; }
.detail-panels :deep(.v-expansion-panel-text__wrapper) { padding: 0 16px 16px 16px; }
.metric-card { border: 1px solid rgba(0,0,0,.08); border-radius: 10px; padding: 10px 12px; background: #fafafa; height: 100%; }
.metric-label { font-size: .72rem; color: rgba(0,0,0,.58); text-transform: uppercase; letter-spacing: .35px; margin-bottom: 3px; }
.metric-value { font-size: .94rem; font-weight: 600; }
.lot-table-wrap { border: 1px solid rgba(0,0,0,.08); border-radius: 10px; overflow: hidden; overflow-x: auto; }
.lot-table { width: 100%; border-collapse: collapse; min-width: 920px; }
.lot-table th, .lot-table td { padding: 10px 12px; border-bottom: 1px solid rgba(0,0,0,.06); font-size: .85rem; }
.lot-table th { background: #f6f7f9; text-align: left; white-space: nowrap; font-weight: 600; }
.lot-table tbody tr:nth-child(even) { background: #fcfcfc; }
.empty-lots { border: 1px dashed rgba(0,0,0,.18); border-radius: 10px; padding: 16px; text-align: center; color: rgba(0,0,0,.6); background: #fafafa; }
@media print {
  .d-print-none { display: none !important; }
  .stat-card, .info-box, .metric-card, .section-header, .lot-table-wrap { break-inside: avoid; }
}
</style>
